import React from 'react'
import i18next from 'i18next'
import {ColumnType} from 'antd/es/table'
import moment from 'moment'
import {Currencies, DATE_FORMAT, TITLE_DAY_FORMAT, TIME_FORMAT} from '@/constants'
import {restOfColumns} from '@/stores/settlements/services/columns'
import {
    TRANSACTION_DATE_COLUMN_KEY,
    TRANSACTION_DATE_COLUMN_WIDTH
} from '@/stores/settlements/constants'
import {Settlement} from '@/stores/settlements/models/Settlement'
import {getAmountWithCurrency} from '@/utils'
import {GroupableItem} from '@/types/grid'
import styles from './styles.scss'

export type PosTransactionColumnType = ColumnType<GroupableItem<Settlement>>

export const dateToDayAndMonth = (rawDate: string) => {
    const date = moment(rawDate)
    const today = moment()
    if (today.format('D') === date.format('D')) {
        return i18next.t('settlements.columnValues.today')
    }
    return date.format(TITLE_DAY_FORMAT)
}

export function checkIfContainsDate(dateColumn: {}, date: string) {
    if (!dateColumn[date]) {
        dateColumn[date] = true
        return true
    }
    return false
}

export const generateColumns = (isMobile, settlements = []) => {
    const rest = restOfColumns(isMobile, settlements)

    return [
        getTransactionDateColumn(),
        ...rest.map((column) => ({
            ...column,
            onCell: (record) => {
                if (record.groupTitle) {
                    return {className: styles.groupHeader}
                }
                return undefined
            }
        }))
    ]
}

export const generatePerDayColumns = (settlementsPerDay = []) => {
    return [
        {
            title: i18next.t('settlements.columnsPerDay.transactionDate'),
            key: 'transactionDate',
            dataIndex: 'transactionDate',
            fixed: 'left' as const,
            width: 180,
            render: (value: string) => Boolean(value) && moment(value).format(DATE_FORMAT)
        },
        {
            title: i18next.t('settlements.columnsPerDay.count'),
            key: 'count',
            dataIndex: 'count',
            width: 90
        },
        {
            title: i18next.t('settlements.columnsPerDay.amount'),
            key: 'amount',
            dataIndex: 'amount',
            render: (value, object) => (
                <b>{getAmountWithCurrency(value, object.currency as Currencies)}</b>
            ),
            width: 140
        },
        {
            title: i18next.t('settlements.columnsPerDay.settlementType'),
            key: 'settlementType',
            dataIndex: 'settlementType',
            width: 160
        },
        {
            title: i18next.t('settlements.columnsPerDay.totalFee'),
            key: 'feeAmount',
            dataIndex: 'feeAmount',
            render: (value, object) => (
                <b>{getAmountWithCurrency(value, object.currency as Currencies)}</b>
            ),
            width: 150
        },
        {
            title: i18next.t('settlements.columns.dccRebate'),
            dataIndex: 'dccRebateAmount',
            key: 'dccRebateAmount',
            width: 140,
            render: (item, object) => <b>{getAmountWithCurrency(item)}</b>
        },
        {
            title: i18next.t('settlements.columnsPerDay.amountToMerchant'),
            key: 'amountToMerchant',
            dataIndex: 'amountToMerchant',
            render: (value, object) => (
                <b>{getAmountWithCurrency(value, object.currency as Currencies)}</b>
            ),
            width: 170
        },
        {
            title: i18next.t('settlements.columnsPerDay.settlementDate'),
            key: 'settlementDate',
            dataIndex: 'settlementDate',
            width: 180,
            render: (value) => value && moment(value).format(DATE_FORMAT)
        }
    ]
}

export const getTransactionDateColumn = (): PosTransactionColumnType => ({
    title: i18next.t('settlements.columns.transactionDate'),
    dataIndex: TRANSACTION_DATE_COLUMN_KEY,
    colSpan: 1,
    key: TRANSACTION_DATE_COLUMN_KEY,
    width: TRANSACTION_DATE_COLUMN_WIDTH,
    fixed: 'left',
    onCell: (record) => {
        if (record.groupTitle) {
            return {className: styles.groupHeader}
        }
        return undefined
    },
    render: (date: string, row) => {
        if (row.groupTitle) {
            return <div> {row.groupTitle}</div>
        }
        return date ? moment(date).format(`${DATE_FORMAT} | ${TIME_FORMAT}`) : null
    }
})
